import { motion, AnimatePresence } from 'framer-motion';
import { UtensilsCrossed, Check, X } from 'lucide-react';
import { usePartners } from '@/hooks/usePartners';
import { useTelegram } from '@/hooks/useTelegram';

/**
 * Присланное партнёром — прямо на главной, принимается одним касанием.
 *
 * Никаких ссылок и переходов: открыл приложение — увидел — нажал. Ровно то,
 * ради чего мы отказались от пересылки через мессенджер.
 *
 * Пусто — не показываем ничего. Пустой блок «здесь пока ничего нет» занимает
 * место у всех ради редкого случая.
 */
export function PartnerInbox({ onAccepted }: { onAccepted?: () => void }) {
  const { incoming, partners, accept, decline } = usePartners();
  const { haptic } = useTelegram();

  if (!incoming.length) return null;

  const nameOf = (id: string) => partners.find((p) => p.id === id)?.name || 'Партнёр';

  return (
    <AnimatePresence initial={false}>
      {incoming.map((s) => {
        const items = s.payload?.items ?? [];
        const kcal = items.reduce((a: number, i: any) => a + (Number(i.calories) || 0), 0);
        const names = items.map((i: any) => i.name).filter(Boolean).join(', ');
        return (
          <motion.div
            key={s.id}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, height: 0, marginBottom: 0 }}
            className="bg-bg-card rounded-xl px-3 py-2.5 mb-3 border border-sky-glow/25"
            style={{ boxShadow: '0 0 16px rgba(61,216,255,0.12)' }}
          >
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-sky-glow/15 flex items-center justify-center shrink-0">
                <UtensilsCrossed size={14} className="text-sky-glow" aria-hidden />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-ink truncate">
                  {nameOf(s.from_user_id)} прислал{names ? `: ${names}` : ' приём пищи'}
                </div>
                <div className="text-[10px] text-ink-dim">{Math.round(kcal)} ккал</div>
              </div>
              <button
                onClick={() => {
                  haptic('medium');
                  accept(s.id);
                  onAccepted?.();
                }}
                className="shrink-0 px-3 py-1.5 rounded-lg bg-emerald-glow text-bg text-xs font-semibold active:scale-95 transition"
              >
                <Check size={13} className="inline -mt-0.5 mr-1" aria-hidden />
                Добавить
              </button>
              <button
                onClick={() => {
                  haptic('light');
                  decline(s.id);
                }}
                aria-label="Отклонить"
                className="shrink-0 p-1.5 rounded-lg text-ink-dim active:scale-90 transition"
              >
                <X size={14} aria-hidden />
              </button>
            </div>
          </motion.div>
        );
      })}
    </AnimatePresence>
  );
}
